import { z } from 'zod';

import { VenueSlotStatusSchema } from '../venue-slot/venue-slot';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * The widest window one request may read.
 *
 * Sixty-two days, because the surface that asks is a **month grid**: six weeks
 * of cells, forty-two days, whose first and last rows reach into the
 * neighbouring months — plus room for a caller that prefetches the next month
 * without a second round trip. A ceiling below forty-two would refuse the
 * ordinary request, which is why the spec pins that floor rather than the number.
 */
export const VENUE_SLOT_WINDOW_MAX_DAYS = 62;

/**
 * One venue's slots inside a window.
 *
 * ## Deliberately NOT `makeListQuery`
 *
 * Every other admin list in this portal is paginated, and this one is not — so
 * the absence is stated rather than left to look like an oversight. The caller is
 * a calendar, and it draws **every** slot in the month it is showing: a
 * `pageSize` would produce a grid that silently stops on the 21st, which is the
 * control-that-lies defect (P25a1's un-sortable header, in a different costume)
 * rather than a list that pages. The bound that keeps the read honest is the
 * window's own width, which is why {@link VENUE_SLOT_WINDOW_MAX_DAYS} exists and
 * why both ends are required: an open-ended window is the whole table.
 *
 * The ceiling is enforced **here** rather than as a typed refusal, because it is
 * a property of the request shape and not a policy the deployment configures — a
 * 422 `validation_failed` naming the field is the right answer, and inventing an
 * error code for it would be a second place the same rule lives.
 *
 * Sorting is not offered either: slots are ordered by time, which is the only
 * order a calendar has.
 */
export const VenueSlotsListQuerySchema = z
  .object({
    /** Inclusive lower bound, an ISO instant. */
    from: z.iso.datetime(),
    /** Exclusive upper bound, an ISO instant. */
    to: z.iso.datetime(),
    status: VenueSlotStatusSchema.optional(),
  })
  .strict()
  .refine((window) => Date.parse(window.to) > Date.parse(window.from), {
    message: 'The window must end after it starts.',
    path: ['to'],
  })
  .refine(
    (window) =>
      Date.parse(window.to) - Date.parse(window.from) <= VENUE_SLOT_WINDOW_MAX_DAYS * MS_PER_DAY,
    {
      message: `A window may span at most ${VENUE_SLOT_WINDOW_MAX_DAYS} days.`,
      path: ['to'],
    },
  );

export type VenueSlotsListQuery = z.infer<typeof VenueSlotsListQuerySchema>;
